import { Autocomplete, Box, Card, Chip, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getStateAlerts, postLeaveOrder } from '../../../../api/orders';
import { editOnBehalfOf, getPendingOrderInfo } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import ManagerChips from '../../../../reusable-components/datagrid/ManagerChips';
import Iconify from '../../../../reusable-components/iconify';
import WeatherInfo from '../../../../reusable-components/weather-info/WeatherInfo';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { categoryIconLookup } from '../../../../utils/cssStyles';
import { fDateTime } from '../../../../utils/formatTime';
import AltAddressDialog from './AltAddressDialog';
import OrderNotesViewModal from '../OrderNotesViewModal';
import axiosInstance from '../../../../utils/axios';
import AdjustBudget from '../AdjustBudgetDate';
import IconWithText from './IconWithText';

OrderInfo.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func
};

export default function OrderInfo({ open, setOpen }) {
  const { id = '' } = useParams();
  const hasFacilityAccess = useScopeCheck(['Facility-Manager'], true);
  const [managerParams, setManagerParams] = useState(null);
  const [copied, setCopied] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { orderById } = useSelector((state) => state.orders || {});
  const dispatch = useDispatch();
  const hasUsersAccess = useScopeCheck(['User-View', 'Admin'], true);
  const {
    facility,
    submittedBy,
    dateAdded,
    onBehalfOf,
    onBehalfOfEmail,
    department,
    items = [],
    facilityBedCount,
    facilityAddress,
    facilityManagerAssignments,
    vendors,
  } = orderById[id] || {};

  const { name, email } = submittedBy || { name: '', email: '' };

  const uniqueVendors = useMemo(() => {
    const vendorIds = new Set(items?.map((item) => item.vendorId));
    return vendors?.filter((vendor) => vendorIds?.has(vendor.id));
  }, [items, vendors]);

  const allowAddressEdits = uniqueVendors?.every((v) => v.allowsAddressEdits);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = (e) => {
    setCopied(false);
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const [occupiedBy, setOccupiedBy] = useState([]);
  const [onBehalfOfList, setOnBehalfOfList] = useState([]);
  const [stateAlerts, setStateAlerts] = useState([]);
  const [weatherInfo, setWeatherInfo] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [hasFetchedWeather, setHasFetchedWeather] = useState(false);

  const addressToDisplay = useMemo(() => {
    if (orderById[id]?.orderAddress) {
      return orderById[id].orderAddress;
    }
    return facilityAddress;
  }, [facilityAddress, orderById, id]);

  useEffect(() => {
    if (id && open) {
      axiosInstance
        .get(`OrderOccupantsAndOnBehalfOfList?orderId=${id}`)
        .then((res) => {
          setOccupiedBy(res.data?.occupants ?? []);
          setOnBehalfOfList(res.data?.onBehalfOfList ?? []);
        })
        .catch(() => enqueueSnackbar('Failed to load occupant list', { variant: 'error' }));
    }
  }, [id, open]);

  // Handle postLeaveOrder when page exits or closes
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      event.preventDefault();
      await postLeaveOrder(id);
    };

    const updateOccupantInterval = setInterval(() => {
      axiosInstance.post(`UpdateOrderOccupant?orderId=${id}`).catch(() => { /* Intentionally blank */ })
    }, 45000);

    window.addEventListener('pagehide', handleBeforeUnload);
    return () => {
      clearInterval(updateOccupantInterval);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [id]);

  useEffect(() => {
    if (!addressToDisplay?.zip) return;

    const fetchWeather = async () => {
      try {
        setLoadingWeather(true);
        const res = await getStateAlerts(addressToDisplay?.zip);

        setWeatherInfo(res?.weather ?? null);
        setStateAlerts(res?.headlines ?? []);
      } catch (e) {
        setWeatherInfo(null);
        setStateAlerts([]);
      } finally {
        setLoadingWeather(false);
        setHasFetchedWeather(true);
      }
    };

    fetchWeather();
  }, [addressToDisplay?.zip]);

  const updateOnBehalfOf = (_, newValue) => {
    dispatch(editOnBehalfOf(id, newValue.value));
  };

  const updateManagerParamsRows = (updatedRow) => {
    setManagerParams((prev) => ({
      ...prev,
      row: {
        ...prev.row,
        ...updatedRow[0],
      },
    }));
  };

  useEffect(() => {
    setManagerParams({
      row: {
        facilityManagerAssignments,
        facilityId: facility?.id,
        id,
      },
      api: { updateRows: updateManagerParamsRows },
    });
  }, [facilityManagerAssignments, facility, id]);

  return (
    <>
      <Box sx={{ minWidth: 0, width: '100%', mb: 2 }}>
        <Card sx={{ p: 1, width: '100%' }}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            onClick={handleClick}
            sx={{ cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontWeight: 'bold',
                  color: '#0288d1',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: 250,
                }}
                title={facility}
              >
                {facility || 'Loading...'}
              </span>
              {stateAlerts?.length > 0 && (
                <Tooltip title={stateAlerts[0]?.headline || 'Weather Alert'}>
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://weather.com/weather/today/l/${addressToDisplay?.zip}`, '_blank');
                    }}
                    size="small"
                    sx={{ ml: 0.5 }}
                  >
                    <Iconify icon={stateAlerts[0]?.icon} width={24} height={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer', ml: 1 }} color="info.main">
              <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
            </Typography>
          </Grid>
          {open && (
            <Box sx={{ ml: 3 }}>
              <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" sx={{ py: 1.5 }}>
                <ScopeGuard scopes={['Edit-Order']} allowAdmin>
                  <Iconify sx={{ mr: 0.5, mb: -0.5 }} icon="fa6-regular:address-card" width={16} height={16} color="secondary" clickable={false} />
                  <Box
                    sx={{ ml: 3, mt: -2, textDecoration: 'underline', color: 'secondary.main', cursor: 'pointer' }}
                    onClick={() => window.open(`https://www.google.com/maps/place/${addressToDisplay?.street}, ${addressToDisplay?.city} ${addressToDisplay?.state} ${addressToDisplay?.zip}`,
                      '_blank', 'noopener,noreferrer')}
                  >
                    <Typography sx={{ py: 0.5 }} component="span" variant="body2">
                      {addressToDisplay?.street && <Typography variant="body2" color={allowAddressEdits ? 'secondary.main' : 'grey.main'} display="block">
                        {addressToDisplay.street}
                        {allowAddressEdits && <Tooltip title={'Edit delivery address'} arrow>
                          <IconButton onClick={(e) => { e.stopPropagation(); handleClickOpen(); }} disableRipple sx={{ p: 0 }} >
                            <Iconify sx={{ mr: 0.5, ml: 1 }} icon="mdi:pencil" width={16} height={16} color="secondary" />
                          </IconButton>
                        </Tooltip>}
                      </Typography>}
                    </Typography>
                    {(addressToDisplay?.city || addressToDisplay?.state || addressToDisplay?.zip) && (
                      <Typography variant="body2" color={allowAddressEdits ? 'secondary.main' : 'grey.main'} display="block">
                        {[addressToDisplay.city, addressToDisplay.state?.value || addressToDisplay.state, addressToDisplay.zip].filter(x => x).join(" ")}
                      </Typography>
                    )}
                    {addressToDisplay?.phoneNumber && <Typography variant="body2" color={allowAddressEdits ? 'secondary.main' : 'grey.main'} display="block">
                      {addressToDisplay.phoneNumber}
                    </Typography>}
                  </Box>
                </ScopeGuard>
                <IconWithText text={`Beds: ${facilityBedCount}`} icon="mingcute:bed-line" />
                <IconWithText text={`Department: ${department}`} icon={categoryIconLookup[department?.toLowerCase()] || 'mdi:calculator'} />
                <IconWithText text={<>
                  Submitted by:
                  <Tooltip title={copied ? `Copied!` : `Copy ${email || ''} to clipboard`}>
                    <Typography
                      component="span"
                      sx={{ ml: 0.5, textDecoration: 'underline', cursor: 'pointer', overflow: "hidden", whiteSpace: "nowrap" }}
                      variant="body2"
                      onClick={() => {
                        navigator.clipboard.writeText(email);
                        setCopied(true);
                      }}
                    >
                      {name}
                    </Typography>
                  </Tooltip>
                </>} icon='mdi:paper-outline' sx={{ whiteSpace: "nowrap", display: "flex" }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 0.5 }}>
                  <Iconify sx={{ mr: 0.5 }} icon="mdi:user-outline" width={16} height={16} color="secondary" clickable={false} />
                  <span style={{ whiteSpace: 'nowrap', marginRight: 4, fontSize: 14 }}>On behalf of:</span>
                  {hasUsersAccess ? (
                    <Autocomplete
                      value={onBehalfOfList.find((u) => u.value?.toLowerCase() === onBehalfOfEmail?.toLowerCase()) ?? null}
                      options={onBehalfOfList}
                      onChange={updateOnBehalfOf}
                      disableClearable
                      sx={{
                        flex: 'none',
                        minWidth: 150,
                        maxWidth: 200,
                        '& .MuiInputBase-root': { borderRadius: 1, background: '#f8fafc', fontSize: '14px', padding: '0 4px', height: 28, minHeight: 28 },
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li
                          {...props}
                          style={{
                            backgroundColor: selected ? 'primary' : 'white',
                            borderRadius: 6,
                            margin: '2px 4px',
                            padding: '2px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 13,
                            minHeight: 24,
                          }}
                        >
                          <Iconify icon="mdi:account-circle" width={16} height={16} style={{ marginRight: 6, color: '#1976d2' }} />
                          <span>{option.label}</span>
                        </li>
                      )}
                      PaperComponent={({ children }) => (
                        <div style={{ borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', marginTop: 4, background: '#fff' }}>
                          {children}
                        </div>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select user"
                          autoComplete="off"
                          sx={{
                            input: { fontSize: '13px', padding: '4px 0' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            minHeight: 28,
                            height: 28,
                          }}
                        />
                      )}
                    />
                  ) : <span style={{ whiteSpace: 'nowrap', fontSize: 14 }}>{onBehalfOf}</span>}
                </Box>

                <IconWithText text={`Submitted on: ${fDateTime(dateAdded)} EST`} icon={'clarity:calendar-line'} />
                <AdjustBudget budgetDate={orderById[id]?.budgetDate || orderById[id]?.dateApproved} orderId={id} />
                <WeatherInfo loading={loadingWeather} hasFetchedWeather={hasFetchedWeather} zip={addressToDisplay?.zip} weatherInfo={weatherInfo} stateAlerts={stateAlerts} />
                {facilityManagerAssignments && <>
                  <IconWithText text={"Managers:"} icon={'hugeicons:manager'} />
                  {hasFacilityAccess && !!managerParams?.row && !!managerParams?.api ? <ManagerChips
                    params={managerParams}
                    type="Orders"
                    hasFacilityAccess={hasFacilityAccess}
                    facilityManagers={facilityManagerAssignments}
                    refetch={() => dispatch(getPendingOrderInfo(id, true))}
                    onDeleteSuccess={() => enqueueSnackbar('Manager deleted successfully', { variant: 'success' })}
                  /> : facilityManagerAssignments.map((manager) => <Chip key={manager.modelId} label={manager.manager} color="primary" size="small" sx={{ ml: 1 }} />)}
                </>}
                {occupiedBy && <>
                  <IconWithText text={"Active viewers:"} icon={'mdi:eye'} />
                  {occupiedBy.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pb: 0.5 }}>
                    {occupiedBy.map((user) => <Chip key={user} label={user} color={'secondary'} size="small" variant="outlined" />)}
                  </Box>}
                </>}
                <Typography variant="body2" sx={{ display: 'block', whiteSpace: 'nowrap', alignItems: 'center', mb: 0.5 }}>
                  <OrderNotesViewModal order={orderById[id]} inOrder /> Notes
                </Typography>
              </Grid>
            </Box>
          )}
        </Card>
      </Box>
      <AltAddressDialog
        open={openDialog}
        setOpenDialog={setOpenDialog}
        handleClose={handleClose}
        handleCloseDialog={handleCloseDialog}
      />
    </>
  );
}
